import * as v0 from '@aidkitorg/types/lib/survey';
import React, { useCallback, useContext } from 'react';
import InterfaceContext, { SupportedLanguage } from '../Context';
import { InfoDict } from '../Questions/Props';
import { useMarkdown } from '../Util';
import buildConditionalContent from '@aidkitorg/types/lib/utils/conditionalContent';
import { useMemo } from 'react';
import { FIELD_REF_REGEX, infoReplacer } from '@aidkitorg/types/lib/util';
import { DataTable } from '@aidkitorg/component-library';

function parseHtmlTableToDataTable(tableHtml: string): { columns: any[], data: Record<string, string>[] } | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(tableHtml, 'text/html');
  const table = doc.querySelector('table');
  if (!table) return null;

  let headerCells = Array.from(table.querySelectorAll('thead th, thead td'));
  const hasHeader = headerCells.length > 0;
  if (!hasHeader) {
    const firstRow = table.querySelector('tr');
    if (firstRow) headerCells = Array.from(firstRow.querySelectorAll('th, td'));
  }
  if (headerCells.length === 0) return null;

  const headers = headerCells.map(th => th.textContent?.trim() || '');
  const columnRules = headerCells.map(th => {
    const raw = (th as HTMLElement).getAttribute('data-conditional-rules');
    if (!raw) return null;
    try { return JSON.parse(raw) as Array<{ op: string; value: string; color: string }>; }
    catch { return null; }
  });
  const bodyRows = hasHeader
    ? Array.from(table.querySelectorAll('tbody tr'))
    : Array.from(table.querySelectorAll('tr')).slice(1);

  const columns = headers.map((header, i) => ({
    id: `col_${i}`,
    header,
    accessorKey: `col_${i}`,
    cell: ({ getValue }: { getValue: () => string }) => <span dangerouslySetInnerHTML={{ __html: getValue() ?? '' }} />,
    ...(columnRules[i] ? { meta: { conditionalRules: columnRules[i] } } : {}),
  }));

  const data: Record<string, string>[] = bodyRows.map(tr => {
    const cells = Array.from(tr.querySelectorAll('td, th'));
    return Object.fromEntries(headers.map((_, i) => {
      const raw = cells[i]?.innerHTML?.trim() ?? '';
      // Replace <p> wrappers TipTap adds to cell content with <br> separators to
      // preserve paragraph separation without the unwanted margins.
      const stripped = raw.replace(/<p>([\s\S]*?)<\/p>/gi, '$1<br>').replace(/(<br\s*\/?>\s*)+$/i, '').trim();
      return [`col_${i}`, stripped];
    }));
  });

  return { columns, data };
}

export function toMarkdownWithReplacements(props: {
  lang: SupportedLanguage,
  content: string | v0.Content | v0.ConditionalContent,
  info: InfoDict,
  uid?: string,
  replacer?: (str: string) => string,
  replaceWithHtml?: {
    regex: RegExp,
    html: string
  },
  collapsible?: boolean,
  noParagraphs?: boolean,
  skipBidirectionalWrapper?: boolean,
  /**
   * When set, a `$field_name` reference with no resolvable value (e.g. a distro
   * field with no corresponding history value) renders blank instead of the raw
   * "$field_name" token. Defaults to off to preserve existing content behavior.
   */
  hideMissingFields?: boolean
}) {

  const pieces = buildConditionalContent({ 
    uid: props.uid, 
    info: props.info, 
    lang: props.lang,
    content: props.content
  });

  // By wrapping substitutions with <bdi> tags like this, we greatly improve the odds
  // that they are displayed in the expected direction (ltr or rtl) regardless of the surrounding text.
  // See: https://www.w3.org/International/articles/inline-bidi-markup/index
  function wrapForDirection(wrappedFunc: (str: string) => string) {
    return (s: string) => `<bdi style="white-space: break-spaces">${wrappedFunc(s)}</bdi>`;
  }

  const replaceInfoVar = (s: string) => infoReplacer(s, { ...props, hideUnresolved: props.hideMissingFields });

  // Replaces uid and variables in text and sanitizes and wraps with directional span before using the
  // replacer function that was passed to useModularMarkdown, if there was one
  const firstReplace = (str: string) => str.replace(FIELD_REF_REGEX, !props.skipBidirectionalWrapper ? wrapForDirection(replaceInfoVar) : replaceInfoVar);

  const replacer = props.replacer ? (str: string) => props.replacer!(firstReplace(str)) : firstReplace;

  return replacer(pieces.join("\n"));
}

/**
 * - Converts Content (Array or Object or String) to a single Markdown JSX Element
 * - Replaces any $variables with the value of props.info[variable]
 */
export function useModularMarkdown(props: {
  content: string | v0.Content | v0.ConditionalContent,
  info: InfoDict,
  uid?: string,
  replacer?: (str: string) => string,
  replaceWithHtml?: {
    regex: RegExp,
    html: string
  },
  collapsible?: boolean,
  noParagraphs?: boolean,
  tableRenderer?: (tableHtml: string) => React.ReactNode,
  hideMissingFields?: boolean
}) {
  const context = useContext(InterfaceContext);

  const { collapsible, noParagraphs } = props;

  const defaultTableRenderer = useCallback((tableHtml: string) => {
    const isTipTapTable = /class="[^"]*tiptap-table/.test(tableHtml);
    if (!isTipTapTable) {
      return <div className="markdown" dangerouslySetInnerHTML={{ __html: tableHtml }} />;
    }
    const parsed = parseHtmlTableToDataTable(tableHtml);
    if (!parsed || parsed.data.length === 0) {
      return <div className="markdown" dangerouslySetInnerHTML={{ __html: tableHtml }} />;
    }
    return <div className="[&_table]:border [&_table]:rounded-md [&_th]:bg-gray-100 overflow-hidden [&_table]:table-fixed [&_td]:[overflow-wrap:anywhere] [&_th]:[overflow-wrap:anywhere]"><DataTable columns={parsed.columns} data={parsed.data} enableColumnReordering={true} conditionalCellBorders /></div>;
  }, []);

  const tableRenderer = props.tableRenderer ?? defaultTableRenderer;

  // Only rebuild conditional content if info or lang changes
  const marked = useMemo(() => toMarkdownWithReplacements({
    lang: context.lang,
    ...props
  }), [context.lang, props.info, props.uid, props.content, props.hideMissingFields]);

  return useMarkdown(marked, collapsible, noParagraphs, props.replaceWithHtml, tableRenderer);
}
